<template>
  <div class="historial-container mt-4">
    <div v-if="paginacion.cantidad && !loading" class="mb-4 px-2 d-flex align-center">
      <v-icon x-small color="grey" class="mr-2">mdi-database-outline</v-icon>
      <span class="grey--text text-caption font-weight-black letter-spacing-1">
        {{ paginacion.cantidad }} INFORMES ENCONTRADOS
      </span>
    </div>

    <div v-if="loading">
      <v-skeleton-loader
        v-for="n in 3"
        :key="n"
        type="list-item-two-line"
        dark
        class="mb-3 bg-dark-refined rounded-xl"
      ></v-skeleton-loader>
    </div>

    <div v-else-if="inventarios.length">
      <div
        v-for="inventario in inventarios"
        :key="inventario.uuid"
        class="report-card mb-3 pa-4 rounded-xl cursor-pointer bg-dark-refined border-subtle d-flex align-center"
        @click="abrirInventario(inventario)"
      >
        <div class="status-icon-wrapper mr-4">
          <v-icon :color="estadoColor(inventario.estado)">
            {{ estadoIcono(inventario.estado) }}
          </v-icon>
        </div>

        <div class="flex-grow-1">
          <div class="d-flex align-center">
            <span class="white--text font-weight-black text-body-1">
              {{ moment(inventario.fecha_inicio).format("DD [de] MMMM, YYYY") }}
            </span>
            <v-chip
              x-small
              :color="estadoChipColor(inventario.estado)"
              class="ml-3 font-weight-black"
              dark
            >
              {{ estadoLabel(inventario.estado) }}
            </v-chip>
          </div>
          
          <div class="d-flex align-center mt-1 grey--text text-caption">
            <span class="mr-4"><v-icon x-small class="mr-1">mdi-clock-outline</v-icon> {{ moment(inventario.fecha_inicio).format("HH:mm") }} hs</span>
            <span class="font-mono"><v-icon x-small class="mr-1">mdi-fingerprint</v-icon> ID: {{ inventario.uuid.split('-')[0] }}</span>
          </div>
        </div>

        <div class="action-area">
          <v-icon color="grey darken-2">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>

    <div v-else class="text-center pa-12 rounded-xl border-subtle-dashed">
      <v-icon size="48" color="grey darken-3">mdi-clipboard-off-outline</v-icon>
      <div class="grey--text text-h6 font-weight-black mt-4">Sin registros</div>
      <div class="grey--text text-caption">No hay inventarios que coincidan con los filtros.</div>
    </div>

    <div v-if="paginado && pages > 1" class="d-flex justify-center mt-8">
      <v-pagination
        v-model="paginacion.page"
        :length="pages"
        circle
        dark
        class="yt-pagination"
        @input="setPage"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import inventarioMixin from "@/mixins/inventario.mixin";
import InventarioAPI from "@/api/inventario";
import moment from "moment";

export default {
  mixins: [inventarioMixin],
  name: "SucursalInventarios",
  props: {
    limite: { type: Number, default: null },
    paginado: { type: Boolean, default: true },
    filtros: {
      type: Object,
      default: () => ({ fecha: null, estado: null }),
    },
  },
  data() {
    return {
      inventarios: [],
      loading: false,
      paginacion: { page: 1, cantidad: 0, rowsPerPage: 10 },
      moment: moment,
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.paginacion.cantidad / this.paginacion.rowsPerPage);
    },
  },
  watch: {
    filtros: {
      deep: true,
      handler() {
        this.paginacion.page = 1;
        this.fetchInventarios();
      },
    },
  },
  methods: {
    fetchInventarios() {
      this.loading = true;
      const params = {};
      if (this.uuidSucursalInventario) {
        params.uuid_sucursal = this.uuidSucursalInventario;
      }

      if (this.paginado) {
        params.page = this.paginacion.page;
        params.page_size = this.paginacion.rowsPerPage;
      } else if (this.limite) {
        params.page_size = this.limite;
      }

      if (this.filtros.fecha) params.fecha_inicio = this.filtros.fecha;
      if (this.filtros.estado) params.estado = this.filtros.estado;

      InventarioAPI.index(params)
        .then((res) => {
          const data = res.data;
          let results = data.results || data;
          // Excluir anulados a menos que se filtre explícitamente por ese estado
          if (!this.filtros.estado) {
            results = results.filter((inv) => inv.estado !== "Anulado");
          }
          this.inventarios = results;
          this.paginacion.cantidad = data.count || this.inventarios.length;
        })
        .finally(() => (this.loading = false));
    },
    setPage(page) {
      this.paginacion.page = page;
      this.fetchInventarios();
    },
    estadoColor(estado) {
      if (estado === "Cerrado") return "green";
      if (estado === "Anulado") return "grey";
      return "blue";
    },
    estadoIcono(estado) {
      if (estado === "Cerrado") return "mdi-check-circle";
      if (estado === "Anulado") return "mdi-cancel";
      return "mdi-progress-clock";
    },
    estadoChipColor(estado) {
      if (estado === "Cerrado") return "green darken-4";
      if (estado === "Anulado") return "grey darken-2";
      return "blue darken-4";
    },
    estadoLabel(estado) {
      if (estado === "Abierto") return "EN CURSO";
      return estado.toUpperCase();
    },
    abrirInventario(inventario) {
      if (inventario.estado !== "Abierto") {
        this.$router.push({
          name: this.rutaInventario('resumen'),
          params: { inventarioUuid: inventario.uuid },
        });
      } else {
        this.$router.push({
          name: this.rutaInventario('iniciado'),
          params: { inventarioUuid: inventario.uuid },
        });
      }
    },
  },
  created() {
    this.fetchInventarios();
  },
};
</script>

<style scoped>
.bg-dark-refined {
  background-color: #161616;
}

.border-subtle {
  border: 1px solid #222 !important;
}

.border-subtle-dashed {
  border: 2px dashed #222 !important;
}

.report-card {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.report-card:hover {
  background-color: #1d1d1d;
  border-color: #333 !important;
  transform: translateX(4px);
}

.status-icon-wrapper {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}

.letter-spacing-1 {
  letter-spacing: 1.2px;
}

.font-mono {
  font-family: 'Roboto Mono', monospace;
}

/* Paginación estilo Dark */
.yt-pagination ::v-deep .v-pagination__item {
  background: transparent !important;
  border: 1px solid #333 !important;
  color: #aaa !important;
  box-shadow: none !important;
}

.yt-pagination ::v-deep .v-pagination__item--active {
  background: white !important;
  color: black !important;
}

.yt-pagination ::v-deep .v-pagination__navigation {
  background: transparent !important;
  box-shadow: none !important;
  border: 1px solid #333 !important;
}
</style>